/**
 * Chemins de l'application, centralisés.
 *
 * Les composants ne construisent jamais d'URL à la main : renommer une route
 * se fait ici, et TypeScript signale tous les appels à corriger.
 *
 * Chemins en français : ils sont visibles dans la barre d'adresse et font
 * partie de l'expérience produit.
 */
export const ROUTES = {
  tasks: '/taches',
  settings: '/parametres',
  holidays: '/parametres/jours-feries',
  holidayImport: '/parametres/jours-feries/importer',
  holidayCreate: '/parametres/jours-feries/nouveau',
  holidayEdit: (id: string) => `/parametres/jours-feries/${id}/modifier`,
  preferences: '/parametres/preferences',
  /**
   * Identité de l'entreprise qui exploite l'instance.
   *
   * `/entreprise` et non `/societe` : `/societes` est le répertoire des
   * tiers, et les deux se confondraient dans la barre d'adresse.
   */
  companyIdentity: '/parametres/entreprise',
  aiEmailHistory: '/parametres/emails',
  account: '/mon-compte',
  home: '/',

  // Authentification
  login: '/connexion',
  forgotPassword: '/mot-de-passe-oublie',

  // Application connectée
  dashboard: '/dashboard',
  /** Répertoire : la liste des sociétés et celle des contacts, en onglets. */
  companies: '/societes',
  /** Saisie d'une société — un écran à part, le formulaire compte treize champs. */
  companyCreate: '/societes/nouvelle',
  company: (id: string) => `/societes/${id}`,

  /**
   * Contacts.
   *
   * Pas de liste à eux : le répertoire les présente déjà, sous l'onglet
   * « Contacts » de `/societes`. Seules la saisie et la fiche ont leur adresse.
   */
  contactCreate: '/contacts/nouveau',
  contact: (id: string) => `/contacts/${id}`,
  candidates: '/candidats',
  candidateImport: '/imports',
  cvContactCompletion: (id: string) => `/imports/cv/${id}/completer`,
  candidateTrash: '/parametres/corbeille-candidats',
  /**
   * Fiche d'un candidat.
   *
   * Fonction plutôt que chaîne : les composants n'assemblent jamais d'URL à la
   * main, et renommer le segment se fait ici seul.
   */
  candidate: (id: string) => `/candidats/${id}`,
  candidateCvs: (id: string) => `/candidats/${id}/cvs`,
  candidateCvUpdate: (id: string) => `/candidats/${id}/cv`,

  adv: '/adv',
  /**
   * Fiche d'un document commercial.
   *
   * Une seule route en lecture : la fiche affiche déjà le type, et un
   * identifiant suffit à la retrouver.
   */
  missionFinancial: (id: string) => '/adv/missions/' + id + '/financier',
  advDocument: (id: string) => `/adv/documents/${id}`,

  /*
   * Saisie : **une route par type**.
   *
   * Un contrat, un PVA et une facture ne demandent ni les mêmes champs ni les
   * mêmes obligations. Leur donner chacun son adresse rend les écrans
   * partageables — « la page de création de facture » désigne quelque chose —
   * et évite un formulaire à bascule dont la moitié des champs serait toujours
   * hors sujet.
   */
  advPropositionCreate: '/adv/propositions/nouvelle',
  advOrderCreate: '/adv/bons-de-commande/nouveau',
  advContractCreate: '/adv/contrats/nouveau',
  advPvaCreate: '/adv/pva/nouveau',
  advInvoiceCreate: '/adv/factures/nouvelle',

  /**
   * Prospection : le portefeuille de contacts sous l'angle du suivi.
   *
   * Distincte de `/societes`, qui répond à « qui connaît-on », et de
   * `/actions`, qui est le journal de tous les échanges.
   */
  prospection: '/prospection',

  /**
   * Comptes rendus d'activité.
   *
   * `/cra` et non `/timesheets` : c'est le mot qu'emploient les consultants
   * et l'ADV, et les chemins font partie de l'expérience produit.
   */
  timesheets: '/cra',
  timesheetSatisfactionSettings: '/parametres/satisfaction-cra',
  timesheet: (id: string) => `/cra/${id}`,
  timesheetSatisfaction: (id: string, stage: string) => `/cra/${id}/satisfaction/${stage}`,

  positioning: (id: string) => '/actions/positionnements/' + id,
  actionEdit: (id: string) => '/actions/' + id + '/modifier',
  actionFinancial: (id: string) => '/actions/' + id + '/financier',
  actions: '/actions',
  /** Saisie d'une action — un écran à part, le formulaire compte douze champs. */
  interviews: '/entretiens',
  actionCreate: '/actions/nouvelle',

  projects: '/projets',
  project: (id: string) => `/projets/${id}`,

  /**
   * Messagerie : les séquences de messages et leurs lancements.
   *
   * `/messagerie` et non `/campagnes` : le mot « campagne » évoque le
   * marketing de masse, alors que l'écran sert aussi à écrire à ses propres
   * consultants. Un seul mot pour les deux usages, et c'est celui-là.
   */
  messaging: '/messagerie',
  messagingSequenceEdit: (id: string) => '/messagerie/sequences/' + id + '/modifier',
  messagingSequenceCreate: '/messagerie/sequences/nouvelle',
  messagingSequence: (id: string) => `/messagerie/sequences/${id}`,
  messagingRun: (id: string) => `/messagerie/lancements/${id}`,

  requirements: '/besoins',
  /** Création d'un besoin — un écran à part, le formulaire compte six sections. */
  requirementCreate: '/besoins/nouveau',
  requirementEdit: (id: string) => `/besoins/${id}/modifier`,
  requirement: (id: string) => `/besoins/${id}`,
} as const;
